// packages/ui/src/components/Breadcrumb/Breadcrumb.tsx

import { Fragment } from 'react';

import { ChevronRight } from 'lucide-react';
import { Link } from 'react-router-dom';

import { cn } from '../../lib/cn';

/**
 * Single item rendered inside a Breadcrumb navigation trail.
 *
 * If `href` is provided, the item is rendered as a link; otherwise it is
 * treated as the current page (`aria-current="page"`).
 */
export interface BreadcrumbItem {
  /** Already-translated label — the Breadcrumb component does NOT call i18n itself. */
  label: string;
  /** Link target. Omit on the last item (current page). */
  href?: string;
}

export interface BreadcrumbProps {
  /** Ordered list of breadcrumb items, from root to current page. */
  items: BreadcrumbItem[];
  /** Extra classes merged with the base navigation classes. */
  className?: string;
}

/**
 * Route-based breadcrumb navigation.
 *
 * Hidden on mobile (`hidden sm:flex`) to preserve vertical space on small
 * screens — the page title in the header carries the location context.
 *
 * @example
 * ```tsx
 * <Breadcrumb
 *   items={[
 *     { label: t('nav.home'), href: '/' },
 *     { label: t('nav.users'), href: '/users' },
 *     { label: t('common:actions.editar') },
 *   ]}
 * />
 * ```
 */
export function Breadcrumb({ items, className }: BreadcrumbProps) {
  if (items.length === 0) return null;

  return (
    <nav
      aria-label="breadcrumb"
      className={cn(
        'hidden sm:flex items-center gap-1 text-sm font-body text-slate-500 min-w-0',
        className,
      )}
    >
      <ol className="flex items-center gap-1 min-w-0">
        {items.map((item, index) => {
          const isLast = index === items.length - 1;
          return (
            <Fragment key={`${item.label}-${index}`}>
              <li className="flex items-center min-w-0">
                {isLast || !item.href ? (
                  <span
                    aria-current={isLast ? 'page' : undefined}
                    className="truncate font-medium text-secondary-950"
                  >
                    {item.label}
                  </span>
                ) : (
                  <Link
                    to={item.href}
                    className="truncate text-slate-500 hover:text-primary-500 transition-colors"
                  >
                    {item.label}
                  </Link>
                )}
              </li>
              {!isLast && (
                <li aria-hidden="true" className="flex items-center text-slate-300">
                  <ChevronRight className="h-3.5 w-3.5 shrink-0" />
                </li>
              )}
            </Fragment>
          );
        })}
      </ol>
    </nav>
  );
}
